/**
 * File upload hook for managing file uploads.
 *
 * Uploads files via chunked upload, reads file descriptions from the
 * complete response, and fires onBatchComplete when all files in a
 * batch finish — providing the agent with a file inventory.
 */

import { useState, useCallback, useRef } from 'react';
import { getAuthToken } from '../context/AuthContext';

const AGENT_URL = process.env.REACT_APP_AGENT_URL || 'http://localhost:8090';

export interface UploadState {
  id: string;
  file: File;
  filename: string;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed' | 'cancelled';
  error?: string;
  description?: string;
  isResuming?: boolean;
}

export interface UploadResult {
  filename: string;
  path: string;
  size: number;
  mime_type?: string;
  description?: string;
}

interface UseFileUploadOptions {
  sessionId: string;
  userId: string;
  agentEndpoint?: string;
  onUploadComplete?: (upload: UploadState) => void;
  onBatchComplete?: (results: UploadResult[]) => void;
}

const CHUNK_SIZE = 1024 * 1024; // 1MB chunks

export function useFileUpload({
  sessionId,
  userId,
  agentEndpoint = AGENT_URL,
  onUploadComplete,
  onBatchComplete,
}: UseFileUploadOptions) {
  const [uploads, setUploads] = useState<UploadState[]>([]);
  const abortControllers = useRef<Map<string, AbortController>>(new Map());
  const batchResults = useRef<UploadResult[]>([]);
  const batchSize = useRef<number>(0);

  const generateId = () => `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const uploadsRef = useRef<UploadState[]>([]);
  uploadsRef.current = uploads;

  const updateUpload = useCallback((id: string, updates: Partial<UploadState>) => {
    setUploads((prev) =>
      prev.map((u) => (u.id === id ? { ...u, ...updates } : u))
    );
  }, []);

  const uploadChunkedFile = useCallback(
    async (upload: UploadState): Promise<UploadResult | null> => {
      const { id, file } = upload;
      const controller = new AbortController();
      abortControllers.current.set(id, controller);

      try {
        // Initialize upload
        const initResponse = await fetch(`${agentEndpoint}/files/upload/init`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}) },
          body: JSON.stringify({
            session_id: sessionId,
            user_id: userId,
            files: [
              {
                filename: file.name,
                size: file.size,
                relative_path: '',
                mime_type: file.type || 'application/octet-stream',
              },
            ],
          }),
          signal: controller.signal,
        });

        if (!initResponse.ok) {
          const errorText = await initResponse.text();
          let errorMessage = errorText;
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.detail || errorJson.message || errorJson.error || errorText;
          } catch {
            // Not JSON, use raw text
          }
          throw new Error(errorMessage);
        }

        const initData = await initResponse.json();
        const uploadInfo = initData.uploads?.[0];
        const upload_id = uploadInfo?.upload_id;
        if (!upload_id) {
          throw new Error('No upload ID returned');
        }

        const serverChunkSize = uploadInfo.chunk_size || CHUNK_SIZE;
        const totalChunks = uploadInfo.total_chunks || Math.ceil(file.size / serverChunkSize);

        // Upload chunks
        for (let i = 0; i < totalChunks; i++) {
          if (controller.signal.aborted) {
            throw new Error('Upload cancelled');
          }

          const start = i * serverChunkSize;
          const end = Math.min(start + serverChunkSize, file.size);
          const chunk = file.slice(start, end);

          const arrayBuffer = await chunk.arrayBuffer();
          const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const checksum = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

          const formData = new FormData();
          formData.append('session_id', sessionId);
          formData.append('chunk_index', i.toString());
          formData.append('checksum', checksum);
          formData.append('file', chunk, file.name);

          const chunkResponse = await fetch(`${agentEndpoint}/files/upload/${upload_id}/chunk`, {
            method: 'POST',
            headers: getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {},
            body: formData,
            signal: controller.signal,
          });

          if (!chunkResponse.ok) {
            const errorText = await chunkResponse.text();
            throw new Error(`Failed to upload chunk ${i + 1}: ${errorText}`);
          }

          const progress = Math.round(((i + 1) / totalChunks) * 100);
          updateUpload(id, { progress });
        }

        // Complete upload — read the response body for description
        const finalizeResponse = await fetch(`${agentEndpoint}/files/upload/${upload_id}/complete?session_id=${encodeURIComponent(sessionId)}`, {
          method: 'POST',
          headers: getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {},
          signal: controller.signal,
        });

        if (!finalizeResponse.ok) {
          throw new Error('Failed to finalize upload');
        }

        const completeData = await finalizeResponse.json();
        const description = completeData.description || undefined;

        updateUpload(id, { status: 'completed', progress: 100, description });
        const completedUpload = uploadsRef.current.find((u) => u.id === id);
        if (completedUpload) {
          onUploadComplete?.({ ...completedUpload, status: 'completed', progress: 100, description });
        }

        return {
          filename: completeData.filename || file.name,
          path: completeData.path || file.name,
          size: completeData.size || file.size,
          mime_type: completeData.mime_type,
          description,
        };
      } catch (error) {
        if (error instanceof Error && error.message === 'Upload cancelled') {
          updateUpload(id, { status: 'cancelled' });
        } else {
          updateUpload(id, {
            status: 'failed',
            error: error instanceof Error ? error.message : 'Upload failed',
          });
        }
        return null;
      } finally {
        abortControllers.current.delete(id);
      }
    },
    [agentEndpoint, sessionId, userId, updateUpload, onUploadComplete]
  );

  const addFiles = useCallback(
    async (files: FileList | File[]) => {
      if (!sessionId || !userId) {
        console.error('Cannot upload files: missing sessionId or userId');
        return;
      }

      const fileArray = Array.from(files);
      const newUploads: UploadState[] = fileArray.map((file) => ({
        id: generateId(),
        file,
        filename: file.name,
        progress: 0,
        status: 'pending' as const,
      }));

      setUploads((prev) => [...prev, ...newUploads]);

      // Track batch
      batchResults.current = [];
      batchSize.current = newUploads.length;

      // Upload each file, collect results
      for (const upload of newUploads) {
        updateUpload(upload.id, { status: 'uploading' });
        const result = await uploadChunkedFile(upload);
        if (result) {
          batchResults.current.push(result);
        }
      }

      // All files in batch done — fire batch callback with collected descriptions
      if (batchResults.current.length > 0 && onBatchComplete) {
        onBatchComplete(batchResults.current);
      }
    },
    [sessionId, userId, updateUpload, uploadChunkedFile, onBatchComplete]
  );

  const cancelUpload = useCallback((uploadId: string) => {
    const controller = abortControllers.current.get(uploadId);
    if (controller) {
      controller.abort();
    }
    updateUpload(uploadId, { status: 'cancelled' });
  }, [updateUpload]);

  const cancelAll = useCallback(() => {
    abortControllers.current.forEach((controller) => controller.abort());
    setUploads((prev) =>
      prev.map((u) =>
        u.status === 'uploading' || u.status === 'pending'
          ? { ...u, status: 'cancelled' as const }
          : u
      )
    );
  }, []);

  const clearCompleted = useCallback(() => {
    setUploads((prev) =>
      prev.filter((u) => u.status !== 'completed' && u.status !== 'failed' && u.status !== 'cancelled')
    );
  }, []);

  const isUploading = uploads.some((u) => u.status === 'uploading' || u.status === 'pending');
  const totalProgress = uploads.length > 0
    ? uploads.reduce((sum, u) => sum + u.progress, 0) / uploads.length
    : 0;

  return {
    uploads,
    addFiles,
    cancelUpload,
    cancelAll,
    clearCompleted,
    isUploading,
    totalProgress,
  };
}
